/**
 * Types for simulation results and detailed module review.
 *
 * This is the single source of truth for all simulation result domain types.
 * API shapes live in lib/simulationResultsApi.ts; mappers in lib/mappers.ts.
 *
 * @module SimulationResultsTypes
 */

import type { AnalysisBreakdown } from '@/lib/analysisBreakdown';

// ============================================================================
// Primitive types
// ============================================================================

export type ExamType = 'telc' | 'goethe' | 'osd';
export type ExamLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type AnalysisStatus = 'pending' | 'running' | 'succeeded' | 'failed';
export type SectionFeedbackStatus = 'pending' | 'complete' | 'failed';

/** Code-derived drill recommendation from section tag histogram. */
export interface RecommendedPractice {
    readonly title: string;
    readonly id: string;
}

/** One section's LLM-generated feedback (Diagnostic Feedback System). */
export interface SectionFeedback {
    readonly section: number;
    readonly status: SectionFeedbackStatus;
    readonly strength_summary?: string | null;
    readonly weakness_summary?: string | null;
    readonly notes_summary?: string | null;
    readonly priority_focus?: string | null;
    readonly recommended_practice?: readonly RecommendedPractice[];
    readonly updated_at?: string | null;
}

// ============================================================================
// Domain result shapes (UI layer)
// ============================================================================

export interface ModuleResult {
    module: 'reading' | 'writing' | 'listening' | 'speaking';
    score: number;
    maxScore: number;
    percentage: number;
    passed: boolean;
    is_graded: boolean;
    analysis_status?: AnalysisStatus | null;
    analysis_error_code?: string | null;
    analysis_attempts?: number | null;
    analysis_can_retry?: boolean | null;
    analysis_updated_at?: string | null;
    /**
     * Diagnostic Feedback System (R/L only): aggregated lifecycle of the
     * per-section feedback rows (pending/complete/failed). Independent of
     * analysis_status, which is the per-answer / W/S lifecycle.
     */
    section_feedback_status?: SectionFeedbackStatus | null;
}

export interface SimulationResult {
    id: string;
    date: string;
    examType: ExamType;
    level: ExamLevel;
    totalScore: number;
    maxTotalScore: number;
    percentage: number;
    passed: boolean;
    duration: number; // in minutes
    modules: ModuleResult[];
}

// ============================================================================
// Display helpers
// ============================================================================

export const examTypeLabels: Record<ExamType, string> = {
    telc: 'telc',
    goethe: 'Goethe-Institut',
    osd: 'ÖSD',
};

export const levelDescriptions: Record<ExamLevel, string> = {
    A1: 'Beginner',
    A2: 'Elementary',
    B1: 'Intermediate',
    B2: 'Upper Intermediate',
    C1: 'Advanced',
    C2: 'Mastery',
};

// ============================================================================
// Module answer review types
// ============================================================================

export interface WordTimestamp {
    readonly word: string;
    readonly start: number;
    readonly end: number;
    readonly issue?: string | null;
    readonly tip?: string | null;
}

export interface ModuleAnswer {
    readonly id: string;
    readonly question_text: string;
    readonly question_type: string;
    readonly explanation?: string;
    readonly context_id?: string | null;
    readonly context_content?: string | null;
    readonly context_name?: string | null;
    readonly context_type?: string | null;
    readonly context_transcript?: string | null;
    readonly context_audio_base64?: string | null;
    readonly context_image_base64?: string | null;
    readonly context_media_url?: string | null;
    readonly context_mime_type?: string | null;
    readonly answer_text?: string;
    readonly answer_audio_base64?: string | null;
    readonly audio_url?: string | null;
    readonly options?: string[];
    readonly expected_answer?: string;
    readonly is_correct?: boolean;
    readonly section?: number;
    readonly question_order?: number;
    /**
     * 1-based position the learner saw this question in during the (possibly
     * shuffled) exam. Used to replay the exact exam order in review. Null/absent
     * for legacy answers, which fall back to `question_order`.
     */
    readonly display_order?: number | null;
    readonly audio_format?: string | null;
    readonly audio_duration_seconds?: number | null;
    readonly points?: number | null;
    readonly analysis_breakdown?: AnalysisBreakdown | null;
    /**
     * Diagnostic Feedback System: chosen error-tag codes for a wrong R/L answer
     * and the resolved learner-facing explanation/resource per tag. Present only
     * when diagnostic profile surfacing is enabled; empty otherwise.
     */
    readonly chosen_tags?: string[];
    readonly diagnostic_feedback?: ModuleAnswerDiagnostic[];
    /** Async LLM diagnosis lifecycle for wrong R/L answers (when profile surfacing on). */
    readonly diagnosis_status?: AnalysisStatus | null;
}

export interface ModuleAnswerDiagnostic {
    readonly tag: string;
    readonly explain: string;
    readonly resource?: {
        readonly title?: string;
        readonly id?: string;
        readonly min_cefr?: string;
    };
}

export interface ContextGroup {
    readonly contextKey: string | null;
    readonly contextInfo: {
        readonly name?: string;
        readonly type?: string;
        readonly content?: string;
        readonly audioBase64?: string;
        readonly imageBase64?: string;
        readonly mediaUrl?: string;
        readonly transcript?: string;
        readonly mimeType?: string;
    };
    readonly answers: ModuleAnswer[];
}
